import { useContext, useEffect, useState } from 'react';
import { UserContext } from '@/shared/contexts/UserContext.js';
import { useApi } from '@shared/services/api.js';
import { useSnackbar } from '@shared/services/SnackbarService/SnackbarServiceContext.js';

export interface ProfileFormData {
  avatarColor: string;
  company: string;
  companySize: string;
  email: string;
  firstName: string;
  hourlyRate: string;
  industry: string;
  lastName: string;
  positionTitle: string;
  showDealList: boolean;
  tagline: string;
  username: string;
}

export const useProfileData = () => {
  const { refetchUser, user } = useContext(UserContext);
  const api = useApi();
  const { showSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reactionStats, setReactionStats] = useState({
    loading: true,
    totalDislikes: 0,
    totalLikes: 0,
  });
  const [totalReviews, setTotalReviews] = useState(0);

  const [formData, setFormData] = useState<ProfileFormData>({
    avatarColor: user?.avatarColor || '#c05a3a',
    company: user?.company || '',
    companySize: user?.companySize || '',
    email: user?.email || '',
    firstName: user?.firstName || '',
    hourlyRate: user?.hourlyRate ? String(user.hourlyRate) : '',
    industry: user?.industry || '',
    lastName: user?.lastName || '',
    positionTitle: user?.positionTitle || '',
    showDealList: user?.userPreference?.showDealList ?? true,
    tagline: user?.tagLine || '',
    username: user?.username || '',
  });

  const handleChange = (field: string) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((previous) => {
        return {
          ...previous,
          [field]: e.target.value,
        };
      });
    };
  };

  const handleToggleChange = (field: string) => {
    return (value: boolean) => {
      setFormData((previous) => {
        return {
          ...previous,
          [field]: value,
        };
      });
    };
  };

  const handleColorChange = (color: string) => {
    setFormData((previous) => {
      return {
        ...previous,
        avatarColor: color,
      };
    });
  };

  const handleSave = async () => {
    if (!user?.id) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        avatarColor: formData.avatarColor,
        company: formData.company,
        email: formData.email,
        firstName: formData.firstName,
        hourlyRate: formData.hourlyRate ? Number(formData.hourlyRate) : null,
        id: user.id,
        lastName: formData.lastName,
        positionTitle: formData.positionTitle,
        tagLine: formData.tagline,
        username: formData.username,
      };

      const response = await api.put('user', payload);

      if (response.error) {
        const errorMessage = response.message || '';
        if (
          errorMessage.toLowerCase().includes('unique') ||
          errorMessage.toLowerCase().includes('duplicate') ||
          errorMessage.toLowerCase().includes('already exists')
        ) {
          showSnackbar('This username is already taken. Please choose a different one.', 'error');
        } else {
          showSnackbar(errorMessage || 'Failed to update profile', 'error');
        }
        setSaving(false);
        return;
      }

      // Update user preferences separately - always send to ensure it's saved
      const currentShowDealList = user.userPreference?.showDealList ?? true;
      if (currentShowDealList !== formData.showDealList) {
        const prefResponse = await api.put(`user/${user.id}/updatePreferences`, {
          preferences: {
            showDealList: formData.showDealList,
          },
        });

        if (prefResponse.error) {
          showSnackbar('Profile updated, but preferences failed to save. Please try again.', 'warning');
        }
      }

      await refetchUser();
      setIsEditing(false);
      showSnackbar('Profile updated successfully!', 'success');
    } catch (error_: unknown) {
      const error = error_ as {
        message?: string;
        response?: { data?: { message?: string } };
      };
      const errorMessage = error?.response?.data?.message || error?.message || '';
      if (
        errorMessage.toLowerCase().includes('unique') ||
        errorMessage.toLowerCase().includes('duplicate') ||
        (errorMessage.toLowerCase().includes('username') && errorMessage.toLowerCase().includes('exists'))
      ) {
        showSnackbar('This username is already taken. Please choose a different one.', 'error');
      } else {
        showSnackbar('Failed to update profile. Please try again.', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      avatarColor: user?.avatarColor || '#c05a3a',
      company: user?.company || '',
      companySize: user?.companySize || '',
      email: user?.email || '',
      firstName: user?.firstName || '',
      hourlyRate: user?.hourlyRate ? String(user.hourlyRate) : '',
      industry: user?.industry || '',
      lastName: user?.lastName || '',
      positionTitle: user?.positionTitle || '',
      showDealList: user?.userPreference?.showDealList ?? true,
      tagline: user?.tagLine || '',
      username: user?.username || '',
    });
    setIsEditing(false);
  };

  useEffect(() => {
    if (user) {
      setFormData({
        avatarColor: user.avatarColor || '#c05a3a',
        company: user.company || '',
        companySize: user.companySize || '',
        email: user.email || '',
        firstName: user.firstName || '',
        hourlyRate: user.hourlyRate ? String(user.hourlyRate) : '',
        industry: user.industry || '',
        lastName: user.lastName || '',
        positionTitle: user.positionTitle || '',
        showDealList: user.userPreference?.showDealList ?? true,
        tagline: user.tagLine || '',
        username: user.username || '',
      });
    }
  }, [user?.id, user?.company, user?.positionTitle, user?.hourlyRate, user?.userPreference?.showDealList]);

  useEffect(() => {
    const fetchReactionStats = async () => {
      if (!user?.id) {
        setReactionStats({
          loading: false,
          totalDislikes: 0,
          totalLikes: 0,
        });
        return;
      }

      try {
        const response = await api.get('/review', {
          params: {
            authorId: user.id,
            limit: 1000,
            searchType: 'user',
          },
        });

        if (response.error || !response.data) {
          setReactionStats({
            loading: false,
            totalDislikes: 0,
            totalLikes: 0,
          });
          return;
        }

        let totalLikes = 0;
        let totalDislikes = 0;

        interface ReviewWithReactions {
          likeDislikeCount?: number;
          likePercentage?: number;
        }

        response.data.forEach((review: ReviewWithReactions) => {
          if (review.likeDislikeCount && review.likePercentage !== undefined) {
            const likes = Math.round(review.likeDislikeCount * review.likePercentage);
            const dislikes = review.likeDislikeCount - likes;

            totalLikes += likes;
            totalDislikes += dislikes;
          }
        });

        setTotalReviews(response.data.length);
        setReactionStats({
          loading: false,
          totalDislikes,
          totalLikes,
        });
      } catch {
        setReactionStats({
          loading: false,
          totalDislikes: 0,
          totalLikes: 0,
        });
      }
    };

    fetchReactionStats();
  }, [user?.id]);

  return {
    formData,
    handleCancel,
    handleChange,
    handleColorChange,
    handleSave,
    handleToggleChange,
    isEditing,
    reactionStats,
    saving,
    setIsEditing,
    totalReviews,
    user,
  };
};
